import { useState, type FormEvent } from 'react'
import type { UpdateAppointmentRequest, VisitRow } from '@fitting-room/contracts'
import { useAppointmentTypes, useBackOffice, useUpdateAppointment } from '../../lib/adminQueries'
import { dollarsFromMinor, minorFromDollars, offeredTypes, optionsWithStored } from '../../lib/appointments'
import { useStaffToken } from '../../lib/staffToken'
import { DatePicker } from './DatePicker'
import { FormField, FormSheet } from './FormSheet'

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Correcting what is on file. Ported from tperent's `EditDetailsDialog`:
 * only what was typed about the customer and the appointment — never the
 * time, the status or who has it, so a typo fix cannot move or reopen
 * anything.
 *
 * A source the owner has since removed stays selectable as itself, so saving
 * the rest of the form leaves it where it was rather than clearing it.
 */
export function EditDetailsDialog({ visit, onClose }: { readonly visit: VisitRow | null; readonly onClose: () => void }) {
  return (
    <FormSheet open={visit !== null} onOpenChange={(next) => (next ? undefined : onClose())} title="Edit details">
      {visit ? <EditForm key={visit.id} visit={visit} onClose={onClose} /> : null}
    </FormSheet>
  )
}

function EditForm({ visit, onClose }: { readonly visit: VisitRow; readonly onClose: () => void }) {
  const { token } = useStaffToken()
  const types = useAppointmentTypes(token).data?.types ?? []
  const sources = useBackOffice(token).data?.bookingSources
  const save = useUpdateAppointment(token, visit.id)

  const [tierKey, setTierKey] = useState(visit.tierKey)
  const [name, setName] = useState(visit.customerName ?? '')
  const [phone, setPhone] = useState(visit.customerPhone ?? '')
  const [email, setEmail] = useState(visit.customerEmail ?? '')
  const [guests, setGuests] = useState(visit.guestCount === undefined ? '' : String(visit.guestCount))
  const [source, setSource] = useState(visit.source ?? '')
  const [weddingDate, setWeddingDate] = useState(visit.weddingDate ?? '')
  const [budget, setBudget] = useState(dollarsFromMinor(visit.budgetMinor))
  const [notes, setNotes] = useState(visit.staffNotes ?? '')
  const [errors, setErrors] = useState<Record<string, string>>({})

  const offered = offeredTypes(types, visit.tierKey)
  const typeOptions = offered.some((t) => t.key === visit.tierKey) ? offered : [...offered, { key: visit.tierKey, label: visit.tierKey }]

  const submit = (event: FormEvent) => {
    event.preventDefault()
    const next: Record<string, string> = {}
    if (!name.trim()) next.name = 'A name, so the appointment can be found again.'
    if (email.trim() && !EMAIL.test(email.trim())) next.email = "That doesn't look like an email address."
    const guestCount = guests.trim() === '' ? null : Number(guests)
    if (guestCount !== null && (!Number.isInteger(guestCount) || guestCount < 0 || guestCount > 30)) {
      next.guests = 'A number of guests, 0 to 30.'
    }
    const budgetMinor = minorFromDollars(budget)
    if (budgetMinor === undefined) next.budget = 'An amount in dollars, like 2,500.'
    setErrors(next)
    if (Object.keys(next).length > 0) return

    const body: UpdateAppointmentRequest = {
      tierKey,
      customerName: name.trim(),
      customerPhone: phone.trim() || null,
      customerEmail: email.trim() || null,
      guestCount,
      source: source || null,
      weddingDate: weddingDate || null,
      budgetMinor: budgetMinor ?? null,
      staffNotes: notes.trim() || null,
    }
    save.mutate(body, { onSuccess: onClose })
  }

  return (
    <form onSubmit={submit} noValidate>
      <FormField label="Appointment type">
        <select className="st-select" value={tierKey} onChange={(event) => setTierKey(event.target.value)}>
          {typeOptions.map((type) => (
            <option key={type.key} value={type.key}>
              {type.label}
            </option>
          ))}
        </select>
      </FormField>
      <FormField label="Customer name" error={errors.name}>
        <input className="st-input" value={name} onChange={(event) => setName(event.target.value)} autoComplete="off" />
      </FormField>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Phone">
          <input className="st-input" type="tel" value={phone} onChange={(event) => setPhone(event.target.value)} autoComplete="off" />
        </FormField>
        <FormField label="Guests" error={errors.guests}>
          <input className="st-input" inputMode="numeric" value={guests} onChange={(event) => setGuests(event.target.value)} />
        </FormField>
      </div>
      <FormField label="Email" error={errors.email}>
        <input className="st-input" type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="off" />
      </FormField>
      <FormField label="Source">
        <select className="st-select" value={source} onChange={(event) => setSource(event.target.value)}>
          <option value="">Not recorded</option>
          {optionsWithStored(sources, visit.source).map((entry) => (
            <option key={entry.key} value={entry.key}>
              {entry.label}
            </option>
          ))}
        </select>
      </FormField>
      <div className="mb-3 flex flex-col gap-1">
        <span className="st-eyebrow">Wedding date</span>
        <div className="flex items-center">
          <DatePicker value={weddingDate} onChange={setWeddingDate} label="Wedding date" placeholder="Not set" onClear={() => setWeddingDate('')} />
        </div>
      </div>
      <FormField label="Budget" error={errors.budget}>
        <input className="st-input" inputMode="decimal" placeholder="$" value={budget} onChange={(event) => setBudget(event.target.value)} />
      </FormField>
      <FormField label="Notes">
        <textarea className="st-input" rows={4} value={notes} onChange={(event) => setNotes(event.target.value)} />
      </FormField>

      {save.isError ? (
        <p className="st-alert mb-3" role="alert">
          {save.error.message}
        </p>
      ) : null}

      <div className="mt-5 flex gap-2">
        <button type="button" className="st-btn-ghost flex-1" onClick={onClose}>
          Cancel
        </button>
        <button type="submit" className="st-btn flex-[2]" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  )
}
